import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import VentePaymentModal from "../gestion-vente/components/VentePaymentModal.tsx";
import RowActionsMenu from "../components/RowActionsMenu";
import CatalogWorkspace, { CatalogDetailsModal, CatalogFacts, CatalogModal, CatalogTable } from "../components/catalog/CatalogWorkspace";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import { catalogApi as apiClient, catalogConfig, readCatalogPages, useCatalogResource, useCatalogSearch } from "../components/catalog/catalogResources";
import { appointmentSlot, catalogMoney, formatServiceDuration, requireReference, verifyCatalogDraft } from "../components/catalog/catalogPresentation";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import useAppointmentFormOptions from "./useAppointmentFormOptions";
import AppointmentFormContent from "./AppointmentFormContent";

const { RangePicker } = DatePicker;

const APPOINTMENT_PERMISSIONS = {
  VIEW: "Gestion Commerciale.RendezVous.view",
  CREATE: "Gestion Commerciale.RendezVous.create",
  EDIT: "Gestion Commerciale.RendezVous.edit",
  DELETE: "Gestion Commerciale.RendezVous.delete",
};

const SALES_CREATE_PERMISSION = "Gestion Commerciale.Ventes.Ventes.create";
const POS_USE_PERMISSION = "Gestion Commerciale.POS.use";
const INCOMING_PAYMENT_CREATE_PERMISSION =
  "Gestion Commerciale.Ventes.PaiementsEntrants.create";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Programmé" },
  { value: "confirmed", label: "Confirmé" },
  { value: "in_progress", label: "En cours" },
  { value: "completed", label: "Terminé" },
  { value: "cancelled", label: "Annulé" },
  { value: "no_show", label: "Absent" },
];

const STATUS_COLOR = {
  scheduled: "blue",
  confirmed: "cyan",
  in_progress: "orange",
  completed: "green",
  cancelled: "red",
  no_show: "volcano",
};

const roundToNextHalfHour = () => {
  const now = dayjs();
  const next = now.minute() < 30 ? now.minute(30) : now.add(1, "hour").minute(0);
  return next.second(0);
};

const toCompanyId = (selectedCompany) =>
  selectedCompany?.id ?? (selectedCompany ? Number(selectedCompany) : null);

const toWarehouseId = (selectedWarehouse) =>
  selectedWarehouse?.id ?? (selectedWarehouse ? Number(selectedWarehouse) : null);

const GestionRendezVous = () => {

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [convertingAppointmentId, setConvertingAppointmentId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    warehouse: "all",
    dateRange: [dayjs().startOf("day"), dayjs().add(7, "day").endOf("day")],
  });

  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form) || {};
  const [page, setPage] = useState(1), [detailId, setDetailId] = useState(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const serviceQuery = useCatalogSearch(serviceSearch), filterSearch = useCatalogSearch(filters.search);
  const contextRef = useRef(""), conversionInFlightRef = useRef(false);
  const invalidateAppointments = useBusinessMutationInvalidation("appointment");
  const invalidateSales = useBusinessMutationInvalidation("sale");
  const { user } = useAuth();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const companyId = useMemo(() => toCompanyId(selectedCompany), [selectedCompany]);
  const selectedWarehouseId = useMemo(
    () => toWarehouseId(selectedWarehouse),
    [selectedWarehouse]
  );

  const hasPermission = useCallback(
    (permissionKey) => user?.permissions?.includes(permissionKey) || false,
    [user]
  );

  const canView = hasPermission(APPOINTMENT_PERMISSIONS.VIEW);
  const canCreate = hasPermission(APPOINTMENT_PERMISSIONS.CREATE);
  const canEdit = hasPermission(APPOINTMENT_PERMISSIONS.EDIT);
  const canDelete = hasPermission(APPOINTMENT_PERMISSIONS.DELETE);
  const canCreateSalesOrder = hasPermission(SALES_CREATE_PERMISSION);
  const canUsePos = hasPermission(POS_USE_PERMISSION);
  const canConvertToSale = canCreateSalesOrder || canUsePos;
  const canCreateIncomingPayment = hasPermission(
    INCOMING_PAYMENT_CREATE_PERMISSION
  );

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      warehouse: selectedWarehouseId ? String(selectedWarehouseId) : "all",
    }));
  }, [selectedWarehouseId, companyId]);

  contextRef.current = companyId + ":" + selectedWarehouseId + ":" + modalOpen;
  useEffect(() => () => { contextRef.current = ""; }, []);
  const scope = { user, companyId, warehouseId: selectedWarehouseId };
  const options = useAppointmentFormOptions(scope, modalOpen && canView, serviceQuery, watchedValues.service_id);
  const warehouseResource = useCatalogResource("catalog-warehouse-options", scope,
    (signal) => readCatalogPages("/api/warehouses", "warehouses", companyId, signal), [], false, canView);
  const services = options.data?.services || [], customers = options.data?.customers || [];
  const staffMembers = options.data?.staff || [], warehouses = options.data?.warehouses || warehouseResource.data || [];
  const refsLoading = options.loading || warehouseResource.loading;
  const appointmentsResource = useCatalogResource("appointment-list", scope, async (signal) => {
    const params = { page, limit: 20, search: filterSearch };
    if (filters.status !== "all") params.status = filters.status;
    if (filters.dateRange?.[0]) params.date_from = filters.dateRange[0].toISOString();
    if (filters.dateRange?.[1]) params.date_to = filters.dateRange[1].toISOString();
    if (filters.warehouse !== "all") params.warehouse_id = filters.warehouse === "__global__" ? "null" : filters.warehouse;
    return (await apiClient.get("/api/service-appointments", catalogConfig(companyId, signal, params))).data;
  }, [page, filterSearch, filters.status, filters.warehouse, filters.dateRange.map((date) => date?.toISOString() || "").join(",")], true, canView);
  const appointments = appointmentsResource.data?.appointments || [], loading = appointmentsResource.loading;
  const fetchAppointments = () => appointmentsResource.refresh().catch(() => {});
  useEffect(() => { setPage(1); }, [companyId, filterSearch, filters.status, filters.warehouse, filters.dateRange]);
  useEffect(() => {
    setModalOpen(false); setEditingAppointment(null); setDetailId(null); setPaymentModalOpen(false); setPaymentOrder(null);
    setServiceSearch(""); form.resetFields();
  }, [companyId, selectedWarehouseId, form]);

  const servicesById = useMemo(() => {
    const map = new Map();
    services.forEach((service) => map.set(Number(service.id), service));
    return map;
  }, [services]);

  const customersById = useMemo(() => {
    const map = new Map();
    customers.forEach((customer) => map.set(Number(customer.id), customer));
    return map;
  }, [customers]);

  const getConvertedOrderAmounts = useCallback((appointment) => {
    const totalRaw = Number(
      appointment?.converted_order_total ?? appointment?.total_price ?? 0
    );
    const paidRaw = Number(appointment?.converted_order_paid_amount ?? 0);
    const dueRaw = Number(appointment?.converted_order_due_amount);

    const total = Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : 0;
    const paid = Number.isFinite(paidRaw) && paidRaw >= 0 ? paidRaw : 0;
    const derivedDue = Math.max(0, total - paid);
    const due =
      Number.isFinite(dueRaw) && dueRaw >= 0
        ? Math.min(dueRaw, Math.max(total, dueRaw))
        : derivedDue;

    return { total, paid, due };
  }, []);

  const openCreateModal = () => {
    if (!companyId) {
      message.warning("Selectionnez d'abord une entreprise.");
      return;
    }

    setEditingAppointment(null);
    form.resetFields();
    form.setFieldsValue({
      service_id: undefined,
      client_id: undefined,
      customer_name: "",
      customer_phone: "",
      staff_user_id: undefined,
      warehouse_id: selectedWarehouseId ? String(selectedWarehouseId) : "__global__",
      starts_at: roundToNextHalfHour(),
      duration_minutes: 30,
      total_price: 0,
      status: "scheduled",
      notes: "",
    });
    setModalOpen(true);
  };

  const openEditModal = (appointment) => {
    setEditingAppointment(appointment);
    form.resetFields();
    form.setFieldsValue({
      service_id: appointment.service_id ? Number(appointment.service_id) : undefined,
      client_id: appointment.client_id ? Number(appointment.client_id) : undefined,
      customer_name: appointment.customer_name || "",
      customer_phone: appointment.customer_phone || "",
      staff_user_id: appointment.staff_user_id
        ? Number(appointment.staff_user_id)
        : undefined,
      warehouse_id: appointment.warehouse_id
        ? String(appointment.warehouse_id)
        : "__global__",
      starts_at: appointment.starts_at ? dayjs(appointment.starts_at) : roundToNextHalfHour(),
      duration_minutes: Number(appointment.duration_minutes || 30),
      total_price: Number(appointment.total_price || 0),
      status: appointment.status || "scheduled",
      notes: appointment.notes || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAppointment(null);
    form.resetFields();
  };

  const handleServiceChange = (serviceId) => {
    const service = servicesById.get(Number(serviceId));
    if (!service) return;

    const updates = {};
    if (service.duration_minutes) {
      updates.duration_minutes = Number(service.duration_minutes);
    }
    if (service.base_price !== undefined && service.base_price !== null) {
      updates.total_price = Number(service.base_price);
    }
    if (service.warehouse_id) {
      updates.warehouse_id = String(service.warehouse_id);
    }
    form.setFieldsValue(updates);
  };

  const handleCustomerChange = (clientId) => {
    const customer = customersById.get(Number(clientId));
    if (!customer) return;
    form.setFieldsValue({
      customer_name: customer.name || "",
      customer_phone: customer.phone || "",
    });
  };

  const handleSubmit = async (values) => {
    if (!companyId) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }

    const payload = {
      company_id: companyId,
      service_id: Number(values.service_id),
      client_id: values.client_id ? Number(values.client_id) : null,
      staff_user_id: values.staff_user_id ? Number(values.staff_user_id) : null,
      warehouse_id:
        values.warehouse_id && values.warehouse_id !== "__global__"
          ? Number(values.warehouse_id)
          : null,
      customer_name: values.customer_name?.trim() || null,
      customer_phone: values.customer_phone?.trim() || null,
      starts_at: values.starts_at?.toISOString(),
      duration_minutes: values.duration_minutes,
      status: values.status,
      total_price: values.total_price,
      notes: values.notes?.trim() || null,
    };

    setSubmitting(true);
    try {
      const fresh = await verifyCatalogDraft(options, form, contextRef);
      requireReference(fresh.services, values.service_id, "Le service");
      requireReference(fresh.customers, values.client_id, "Le client");
      requireReference(fresh.staff, values.staff_user_id, "Le praticien");
      requireReference(fresh.warehouses, values.warehouse_id, "L’établissement");
      if (editingAppointment) {
        await apiClient.put(
          `/api/service-appointments/${editingAppointment.id}`,
          payload
        );
        message.success("Rendez-vous mis a jour avec succes.");
      } else {
        await apiClient.post("/api/service-appointments", payload);
        message.success("Rendez-vous cree avec succes.");
      }
      void invalidateAppointments();
      closeModal();
      fetchAppointments();
    } catch (error) {
      const backendMessage = error.response?.data?.error;
      message.error(backendMessage || error.message || "Erreur lors de la sauvegarde du rendez-vous.");
      console.error("[RENDEZVOUS] Error saving appointment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (appointment) => {
    Modal.confirm({
      title: "Supprimer ce rendez-vous ?",
      content: "Cette action est definitive.",
      okText: "Supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      onOk: async () => {
        try {
          await apiClient.delete(`/api/service-appointments/${appointment.id}`);
          void invalidateAppointments();
          message.success("Rendez-vous supprime avec succes.");
          fetchAppointments();
        } catch (error) {
          const backendMessage = error.response?.data?.error;
          message.error(backendMessage || "Erreur lors de la suppression.");
          console.error("[RENDEZVOUS] Error deleting appointment:", error);
        }
      },
    });
  };

  const handleConvertToSale = async (appointment) => {
    if (!companyId) {
      message.error("Aucune entreprise selectionnee.");
      return;
    }
    if (!canConvertToSale || !canEdit) {
      message.error(
        "Permissions insuffisantes pour convertir ce rendez-vous en vente."
      );
      return;
    }

    const appointmentStatus = String(appointment.status || "").trim().toLowerCase();
    if (appointmentStatus === "cancelled" || appointmentStatus === "no_show") {
      message.error("Un rendez-vous annule ou absent ne peut pas etre converti.");
      return;
    }

    if (appointment.converted_order_id) {
      message.info("Ce rendez-vous est deja converti en commande.");
      return;
    }

    if (conversionInFlightRef.current) return;
    conversionInFlightRef.current = true; setConvertingAppointmentId(Number(appointment.id));
    const capturedContext = contextRef.current;
    let service;
    try {
      appointment = (await apiClient.get("/api/service-appointments/" + appointment.id, catalogConfig(companyId))).data;
      if (appointment.converted_order_id || ["cancelled", "no_show"].includes(appointment.status)) throw new Error("Ce rendez-vous ne peut plus être converti. Actualisez le tableau.");
      service = (await apiClient.get("/api/services/" + appointment.service_id, catalogConfig(companyId))).data;
      if (capturedContext !== contextRef.current) throw new Error("Le contexte a changé. Rouvrez le rendez-vous.");
    } catch (error) {
      conversionInFlightRef.current = false; setConvertingAppointmentId(null);
      message.error(error.response?.data?.error || error.message); return;
    }
    const serviceId = Number(appointment.service_id || service?.id || 0);
    if (!serviceId || !service) {
      message.error("Le service de ce rendez-vous est introuvable.");
      conversionInFlightRef.current = false; setConvertingAppointmentId(null); return;
    }

    const clientId = appointment.client_id ? Number(appointment.client_id) : null;
    if (!clientId) {
      message.error(
        "Le rendez-vous doit etre rattache a un client avant conversion."
      );
      conversionInFlightRef.current = false; setConvertingAppointmentId(null); return;
    }

    const warehouseId = appointment.warehouse_id
      ? Number(appointment.warehouse_id)
      : service?.warehouse_id
      ? Number(service.warehouse_id)
      : selectedWarehouseId
      ? Number(selectedWarehouseId)
      : null;
    if (!warehouseId) {
      message.error(
        "Aucun établissement n'est defini pour ce rendez-vous. Renseignez l'établissement puis reessayez."
      );
      conversionInFlightRef.current = false; setConvertingAppointmentId(null); return;
    }

    const unitPriceRaw = Number(appointment.total_price || service?.base_price || 0);
    const unitPrice =
      Number.isFinite(unitPriceRaw) && unitPriceRaw >= 0 ? unitPriceRaw : 0;
    const orderType = canCreateSalesOrder ? "sales" : "POS";

    const payload = {
      company_id: companyId,
      invoice_type: "standard",
      order_type: orderType,
      order_date: new Date().toISOString(),
      warehouse_id: warehouseId,
      user_id: clientId,
      staff_user_id: appointment.staff_user_id
        ? Number(appointment.staff_user_id)
        : null,
      tax_id: null,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      manual_discount: 0,
      loyalty_discount: 0,
      loyalty_discount_rate: 0,
      loyalty_discount_applied: 0,
      shipping: 0,
      subtotal: unitPrice,
      total: unitPrice,
      paid_amount: 0,
      due_amount: unitPrice,
      payment_status: "Non paye",
      order_status: "delivered",
      total_items: 1,
      total_quantity: 1,
      notes: `Conversion rendez-vous #${appointment.id}`,
      terms_condition: "",
      items: [
        {
          item_type: "service",
          product_id: null,
          service_id: serviceId,
          item_name: service.name || appointment.service_name,
          quantity: 1,
          unit_price: unitPrice,
          single_unit_price: unitPrice,
          unit_id: null,
          tax_id: null,
          tax_rate: 0,
          tax_type: null,
          total_tax: 0,
          discount_rate: 0,
          total_discount: 0,
          subtotal: unitPrice,
        },
      ],
    };

    setConvertingAppointmentId(Number(appointment.id));
    try {
      const orderResponse = await apiClient.post("/api/orders", payload);
      const orderId = Number(
        orderResponse.data?.orderId || orderResponse.data?.id || 0
      );
      if (!orderId) {
        throw new Error("Commande creee mais identifiant introuvable.");
      }

      await apiClient.post(`/api/service-appointments/${appointment.id}/link-order`, {
        order_id: orderId,
      });

      const invoiceNumber = orderResponse.data?.invoice_number;
      message.success(
        invoiceNumber
          ? `Rendez-vous converti en vente (${invoiceNumber}).`
          : "Rendez-vous converti en vente avec succes."
      );
      void invalidateAppointments(); void invalidateSales();
      fetchAppointments();
    } catch (error) {
      const backendMessage =
        error.response?.data?.error || error.response?.data?.message;
      message.error(
        backendMessage || "Erreur lors de la conversion du rendez-vous."
      );
      console.error("[RENDEZVOUS] Error converting appointment:", error);
    } finally {
      conversionInFlightRef.current = false;
      setConvertingAppointmentId(null);
    }
  };

  const openPaymentModal = (appointment) => {
    if (!canCreateIncomingPayment) {
      message.error(
        "Vous n'avez pas la permission d'enregistrer un paiement entrant."
      );
      return;
    }

    const orderId = Number(appointment.converted_order_id || 0);
    if (!orderId) {
      message.warning("Ce rendez-vous doit d'abord etre converti en vente.");
      return;
    }

    const customerId = Number(appointment.client_id || 0);
    if (!customerId) {
      message.error("Client introuvable pour cette commande.");
      return;
    }

    const warehouseId = Number(
      appointment.converted_order_warehouse_id ||
        appointment.warehouse_id ||
        selectedWarehouseId ||
        0
    );
    if (!warehouseId) {
      message.error(
        "Établissement introuvable pour cette commande. Impossible d'encaisser."
      );
      return;
    }

    const { total, paid, due } = getConvertedOrderAmounts(appointment);
    if (due <= 0.0001) {
      message.info("Cette commande est deja totalement reglee.");
      return;
    }

    setPaymentOrder({
      id: orderId,
      invoice_number:
        appointment.converted_order_invoice_number || `#${orderId}`,
      company_id: companyId,
      warehouse_id: warehouseId,
      user_id: customerId,
      total,
      paid_amount: paid,
      due_amount: due,
      payment_status:
        appointment.converted_order_payment_status || "Partiellement paye",
    });
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPaymentOrder(null);
  };

  const handlePaymentAdded = async () => {
    await invalidateAppointments();
    await fetchAppointments();
  };

  const serviceOptions = useMemo(
    () =>
      services.map((service) => ({
        value: Number(service.id),
        label: service.name,
      })),
    [services]
  );

  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: Number(customer.id),
        label: customer.name,
      })),
    [customers]
  );

  const staffOptions = useMemo(
    () =>
      staffMembers.map((staff) => ({
        value: Number(staff.id),
        label: staff.name,
      })),
    [staffMembers]
  );

  const warehouseOptions = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        value: String(warehouse.id),
        label: warehouse.name,
      })),
    [warehouses]
  );

  const actions = (record) => [
    { key: "detail", icon: <EyeOutlined />, label: "Voir le détail", onClick: () => setDetailId(record.id) },
    canEdit && canConvertToSale && !record.converted_order_id && { key: "convert", icon: <ShoppingCartOutlined />,
      label: Number(convertingAppointmentId) === Number(record.id) ? "Conversion en cours…" : "Convertir en vente",
      disabled: Boolean(convertingAppointmentId) || ["cancelled", "no_show"].includes(record.status), onClick: () => handleConvertToSale(record) },
    canCreateIncomingPayment && record.converted_order_id && getConvertedOrderAmounts(record).due > 0.0001 &&
      { key: "payment", icon: <DollarOutlined />, label: "Encaisser", onClick: () => openPaymentModal(record) },
    canEdit && { key: "edit", icon: <EditOutlined />, label: "Modifier", onClick: () => openEditModal(record) },
    canDelete && { key: "delete", icon: <DeleteOutlined />, label: "Supprimer", danger: true, onClick: () => handleDelete(record) },
  ].filter(Boolean);
  const columns = [
    { title: "Date", dataIndex: "starts_at", key: "date", width: 126, render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "—" },
    { title: "Créneau", key: "slot", width: 154, render: (_, row) => appointmentSlot(row) },
    { title: "Client", key: "client", render: (_, row) => row.customer_name || row.client_name_ref || "—" },
    { title: "Service", dataIndex: "service_name", key: "service" },
    { title: "Praticien", dataIndex: "staff_name", key: "staff", width: 145, render: (value) => value || "Non assigné" },
    { title: "Statut", dataIndex: "status", key: "status", width: 126, render: (value) => <Tag color={STATUS_COLOR[value]}>{STATUS_OPTIONS.find((row) => row.value === value)?.label || value}</Tag> },
    { title: "Durée", dataIndex: "duration_minutes", key: "duration", width: 110, render: formatServiceDuration },
    { title: "Actions", key: "actions", width: 76, align: "center", render: (_, row) =>
      <RowActionsMenu ariaLabel={"Actions du rendez-vous " + row.id} items={actions(row)} loading={Number(convertingAppointmentId) === Number(row.id)} /> },
  ];

  if (!canView) return <Alert type="warning" showIcon message="Vous n’avez pas la permission d’afficher les rendez-vous." />;
  if (!companyId) return <Alert type="info" showIcon message="Sélectionnez une entreprise pour gérer les rendez-vous." />;
  return <CatalogWorkspace title="Rendez-vous" subtitle="Organisez les prestations et suivez les rendez-vous."
    action={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Nouveau rendez-vous</Button>}>
    <div className="catalog-filters"><label className="catalog-filter catalog-filter--search"><span>Rechercher</span>
      <Input allowClear prefix={<SearchOutlined />} placeholder="Rechercher un client, un service, un praticien" value={filters.search}
        onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))} /></label>
      <label className="catalog-filter"><span>Statut</span><Select value={filters.status} onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
        options={[{ value: "all", label: "Tous les statuts" }, ...STATUS_OPTIONS]} /></label>
      <label className="catalog-filter"><span>Établissement</span><Select value={filters.warehouse} loading={refsLoading}
        onChange={(warehouse) => setFilters((prev) => ({ ...prev, warehouse }))}
        options={[{ value: "all", label: "Tous les établissements" }, { value: "__global__", label: "Global entreprise" }, ...warehouseOptions]} /></label>
      <label className="catalog-filter catalog-filter--range"><span>Période</span><RangePicker showTime format="DD/MM/YYYY HH:mm" value={filters.dateRange}
        onChange={(range) => setFilters((prev) => ({ ...prev, dateRange: range || [null, null] }))} /></label>
    </div>
    <BusinessResourceNotice resource={appointmentsResource} label="les rendez-vous" />
    <div className="catalog-count">{appointmentsResource.data?.pagination?.totalItems ?? appointments.length} rendez-vous</div>
    <CatalogTable columns={columns} dataSource={appointments} loading={loading} onDetail={(row) => setDetailId(row.id)}
      pagination={{ current: page, pageSize: 20, total: appointmentsResource.data?.pagination?.totalItems ?? appointments.length, onChange: setPage }} />
    <p className="catalog-list-note">Cliquez sur une ligne pour consulter le détail.</p>
    <CatalogModal open={modalOpen} onCancel={() => { if (!submitting) closeModal(); }}
      title={editingAppointment ? "Modifier le rendez-vous" : "Nouveau rendez-vous"} subtitle="Choisissez la prestation, le créneau et le client."
      footer={<><Button onClick={closeModal} disabled={submitting}>Annuler</Button><Button type="primary" loading={submitting}
        disabled={options.loading || Boolean(options.error)} onClick={() => form.submit()}>{editingAppointment ? "Enregistrer les modifications" : "Créer le rendez-vous"}</Button></>}>
      <BusinessResourceNotice resource={options} label="les choix du rendez-vous" />
      <Form form={form} size="large" layout="vertical" onFinish={handleSubmit}>
        <AppointmentFormContent form={form} values={watchedValues} editing={editingAppointment} serviceOptions={serviceOptions}
          customerOptions={customerOptions} staffOptions={staffOptions} warehouseOptions={warehouseOptions}
          statuses={STATUS_OPTIONS} statusColors={STATUS_COLOR} onServiceChange={handleServiceChange}
          onCustomerChange={handleCustomerChange} onServiceSearch={setServiceSearch} />
      </Form>
    </CatalogModal>
    <CatalogDetailsModal open={Boolean(detailId)} recordId={detailId} title="Détails du rendez-vous" resourceName="appointment-detail"
      scope={{ userId: user?.id, companyId, warehouseId: selectedWarehouseId }} onClose={() => setDetailId(null)} canEdit={canEdit} onEdit={openEditModal}
      load={async (id, signal) => { const response = await apiClient.get("/api/service-appointments/" + id, catalogConfig(companyId, signal));
        if (response.data.company_id != null && String(response.data.company_id) !== String(companyId)) throw new Error("Le contexte du rendez-vous a changé."); return response.data; }}
      render={(row) => <><CatalogFacts items={[["Client", row.customer_name || row.client_name_ref], ["Téléphone", row.customer_phone],
        ["Service", row.service_name], ["Praticien", row.staff_name || "Non assigné"], ["Date", dayjs(row.starts_at).format("DD/MM/YYYY")],
        ["Créneau", appointmentSlot(row)], ["Durée", formatServiceDuration(row.duration_minutes)],
        ["Statut", STATUS_OPTIONS.find((option) => option.value === row.status)?.label || row.status],
        ["Établissement", row.warehouse_name || "Global entreprise"], ["Tarif", catalogMoney(row.total_price)],
        ["Vente liée", row.converted_order_invoice_number || (row.converted_order_id ? "#" + row.converted_order_id : "Non facturé")],
        ["Paiement", !row.converted_order_id ? "Non facturé" : getConvertedOrderAmounts(row).due <= 0.0001 ? "Payé" : row.converted_order_payment_status || "Non payé"],
        ["Montant payé", row.converted_order_id ? catalogMoney(getConvertedOrderAmounts(row).paid) : "—"],
        ["Reste à payer", row.converted_order_id ? catalogMoney(getConvertedOrderAmounts(row).due) : "—"]]} />
        <p className="catalog-detail-description">{row.notes || "Aucune note renseignée."}</p></>} />
    <VentePaymentModal visible={paymentModalOpen} onClose={closePaymentModal} order={paymentOrder} onPaymentAdded={handlePaymentAdded} />
  </CatalogWorkspace>;
};
export default GestionRendezVous;
